import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search, Loader2, Eye, ChevronLeft, ChevronRight, RefreshCw,
  ChevronDown, ChevronUp, Building2, Phone, Mail, Globe,
  MapPin, Users, FileText, User, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../services/api';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function DField({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
      <p className="text-sm font-medium text-foreground mt-0.5">{value}</p>
    </div>
  );
}

function statusCls(s) {
  const v = (s || '').toLowerCase();
  if (v === 'active')   return 'bg-emerald-100 text-emerald-800 border-emerald-200';
  if (v === 'pending')  return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (v === 'suspended') return 'bg-orange-100 text-orange-800 border-orange-200';
  if (['inactive','expired','revoked','cancelled'].includes(v)) return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-600 border-gray-200';
}

// ── Expandable license card ───────────────────────────────────────────────────
function LicenseCard({ license: l, idx }) {
  const [open, setOpen] = useState(idx === 0);
  const contacts = l.contacts ?? [];
  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-muted/30 hover:bg-muted/60 transition-colors text-left"
      >
        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
          ${l.licenseStatus?.toLowerCase() === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
          {idx + 1}
        </span>
        <div className="flex-1 flex flex-wrap items-center gap-2 min-w-0">
          <span className="font-mono text-sm font-semibold">{l.licenseNumber || `License ${idx + 1}`}</span>
          {l.licenseStateAbbr && <Badge variant="secondary" className="text-xs">{l.licenseStateAbbr}</Badge>}
          {l.licenseType && <span className="text-xs text-muted-foreground">{l.licenseType}</span>}
          {l.licenseStatus && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full border font-medium ${statusCls(l.licenseStatus)}`}>
              {l.licenseStatus}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0 text-xs text-muted-foreground">
          {contacts.length > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{contacts.length}</span>}
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="px-4 py-4 bg-white border-t border-border space-y-4">
          {/* License core info */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-3">
            <DField label="Type"             value={l.licenseType} />
            <DField label="Designation"      value={l.licenseDesignation} />
            <DField label="Vertical"         value={l.vertical} />
            <DField label="Category"         value={l.category} />
            <DField label="Activity"         value={l.activityOrFacility} />
            <DField label="Term"             value={l.licenseTerm} />
            <DField label="Operational Status" value={l.operationalStatus} />
            <DField label="Issue Date"       value={fmtDate(l.issueDate)} />
            <DField label="Expiration"       value={fmtDate(l.expirationDate)} />
            <DField label="First Occurrence" value={fmtDate(l.firstOccurrence)} />
            <DField label="Renewal Due"      value={fmtDate(l.renewalDueDate)} />
            <DField label="License State"    value={l.licenseStateAbbr} />
          </div>

          {/* Business info */}
          {(l.businessName || l.email || l.phone || l.website || l.streetAddress) && (
            <div className="pt-3 border-t border-dashed border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Business Info</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
                <DField label="Business Name" value={l.businessName} />
                <DField label="DBA"           value={l.dbaName} />
                <DField label="Phone"         value={l.phone} />
                <DField label="Email"         value={l.email} />
                {l.website && (
                  <div className="col-span-2">
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Website</p>
                    <a href={l.website} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:underline flex items-center gap-1 mt-0.5">
                      <Globe className="w-3 h-3" />{l.website}
                    </a>
                  </div>
                )}
                {l.streetAddress && (
                  <div className="col-span-2 sm:col-span-3">
                    <DField label="Address"
                      value={[l.streetAddress, l.city, l.county, l.zipCode, l.addressStateAbbr].filter(Boolean).join(', ')}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Social */}
          {(l.instagram || l.facebook || l.linkedin || l.twitter) && (
            <div className="pt-3 border-t border-dashed border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Social</p>
              <div className="flex flex-wrap gap-3">
                {[['Instagram', l.instagram], ['Facebook', l.facebook], ['LinkedIn', l.linkedin], ['Twitter', l.twitter]]
                  .filter(([, v]) => v)
                  .map(([label, url]) => (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-emerald-600 hover:underline">
                      <ExternalLink className="w-3 h-3" />{label}
                    </a>
                  ))}
              </div>
            </div>
          )}

          {/* Contacts */}
          {contacts.length > 0 && (
            <div className="pt-3 border-t border-dashed border-border">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Contacts ({contacts.length})
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {contacts.map((c, ci) => (
                  <div key={c.operatorContactId ?? ci}
                    className="flex items-start gap-3 p-3 rounded-lg bg-muted/30 border border-border/60">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <User className="w-4 h-4 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">
                        {[c.contactFirstName, c.contactLastName].filter(Boolean).join(' ') || `Contact ${ci + 1}`}
                      </p>
                      {c.contactDesignation && <p className="text-xs text-muted-foreground">{c.contactDesignation}</p>}
                      {c.contactPhone && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                          <Phone className="w-3 h-3 shrink-0" />{c.contactPhone}
                        </p>
                      )}
                      {c.contactEmail && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3 shrink-0" />{c.contactEmail}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Full operator detail view (used inside dialog) ────────────────────────────
function OperatorDetailView({ op, onClose }) {
  const [tab, setTab] = useState('licenses');
  const licenses      = op.licenses ?? [];
  const allStates     = [...new Set(licenses.map(l => l.licenseStateAbbr).filter(Boolean))];
  const activeCount   = licenses.filter(l => l.licenseStatus?.toLowerCase() === 'active').length;
  const totalContacts = licenses.reduce((s, l) => s + (l.contacts?.length ?? 0), 0);

  return (
    <div className="flex flex-col h-full max-h-[88vh]">
      {/* Header */}
      <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-green-500 text-white shrink-0 rounded-t-lg">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate">{op.corporateName || '—'}</h2>
            {(() => {
              const fl = licenses[0];
              return fl?.businessName && fl.businessName !== op.corporateName
                ? <p className="text-emerald-100 text-sm truncate">{fl.businessName}{fl.dbaName && ` · DBA: ${fl.dbaName}`}</p>
                : null;
            })()}
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors shrink-0 text-white">
            ✕
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-3 text-sm">
          <span className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
            <FileText className="w-3.5 h-3.5" />
            {licenses.length} license{licenses.length !== 1 ? 's' : ''}
            {activeCount > 0 && <span className="text-emerald-200">({activeCount} active)</span>}
          </span>
          {allStates.length > 0 && (
            <span className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <MapPin className="w-3.5 h-3.5" />{allStates.join(', ')}
            </span>
          )}
          {totalContacts > 0 && (
            <span className="flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1">
              <Users className="w-3.5 h-3.5" />{totalContacts} contact{totalContacts !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border shrink-0 bg-background">
        {[['licenses', FileText, `Licenses (${licenses.length})`], ['corporate', Building2, 'Corporate Info']].map(([key, Icon, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors
              ${tab === key ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <Icon className="w-4 h-4" />{label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-background">
        {tab === 'licenses' && (
          licenses.length === 0
            ? <div className="flex flex-col items-center justify-center h-40 text-muted-foreground">
                <FileText className="w-10 h-10 mb-2 opacity-30" /><p>No licenses on record</p>
              </div>
            : licenses.map((l, i) => <LicenseCard key={l.operatorLicenseId ?? i} license={l} idx={i} />)
        )}

        {tab === 'corporate' && (
          <div className="space-y-4">
            {/* Identity */}
            <section className="rounded-xl border border-border p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Identity</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <DField label="Corporate Name"  value={op.corporateName} />
                <DField label="# Licenses"      value={op.licenseCount ?? licenses.length} />
                <DField label="# States"        value={op.licenseStateCount ?? allStates.length} />
                <DField label="# License Types" value={op.licensetypeCount} />
                {op.notes && <div className="col-span-2"><DField label="Notes" value={op.notes} /></div>}
              </div>
            </section>

            {/* Primary corporate contact person */}
            {(op.corpFirstName || op.corpLastName || op.corpDesignation) && (
              <section className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Primary Contact Person</p>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <User className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <p className="font-semibold">{[op.corpFirstName, op.corpLastName].filter(Boolean).join(' ') || '—'}</p>
                    {op.corpDesignation && <p className="text-sm text-muted-foreground">{op.corpDesignation}</p>}
                  </div>
                </div>
              </section>
            )}

            {/* Corp contact details */}
            <section className="rounded-xl border border-border p-4 space-y-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Corporate Contact</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                <DField label="Phone" value={op.corpPhone} />
                <DField label="Email" value={op.corpEmail} />
                {op.corpWebsite && (
                  <div className="col-span-2">
                    <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">Website</p>
                    <a href={op.corpWebsite} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-emerald-600 hover:underline flex items-center gap-1 mt-0.5">
                      <Globe className="w-3 h-3" />{op.corpWebsite}
                    </a>
                  </div>
                )}
              </div>
            </section>

            {/* Social */}
            {(op.corpFB || op.corpInsta || op.corpTwitter || op.corpLinkedIn) && (
              <section className="rounded-xl border border-border p-4 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Social Media</p>
                <div className="grid grid-cols-2 gap-3">
                  {[['Facebook', op.corpFB], ['Instagram', op.corpInsta],
                    ['Twitter / X', op.corpTwitter], ['LinkedIn', op.corpLinkedIn]]
                    .filter(([, url]) => url)
                    .map(([label, url]) => (
                      <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-sm text-emerald-600 hover:underline">
                        <ExternalLink className="w-3 h-3" />{label}
                      </a>
                    ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t bg-background shrink-0 rounded-b-lg">
        <Button variant="outline" className="w-full" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

export default function SearchOperatorsPage() {
  const [searchParams, setSearchParams] = useState({
    businessName: '',
    states: [],
  });
  const [operators, setOperators] = useState([]);
  const [assignedOperators, setAssignedOperators] = useState([]);
  const [selectedOperatorDetails, setSelectedOperatorDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [quotaInfo, setQuotaInfo] = useState({
    totalAssignedThisMonth: 0,
    remainingQuota: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 100,
    totalRecords: 0,
    totalPages: 0,
  });
  const [facets, setFacets] = useState({
    states: [],
    licenseTypes: [],
    categories: [],
  });

  const [allStates, setAllStates] = useState([]);

  // Load operators and states on mount
  useEffect(() => {
    loadOperators();
    apiClient.get('/onboarding/states').then(r => {
      const list = r.data?.data ?? r.data ?? [];
      setAllStates(list.map(s => ({ code: s.abbreviation, name: s.name })));
    }).catch(() => {});
  }, []);

  const loadOperators = async (page = 1) => {
    setIsLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('pageSize', pagination.pageSize);
      params.append('sortDirection', 'asc');
      params.append('sortBy', 'state');
      
      // Add search filters if any
      if (searchParams.businessName) {
        params.append('businessName', searchParams.businessName);
      }
      searchParams.states.forEach(state => {
        params.append('States', state);
      });

      const response = await apiClient.get(`/operators/search?${params.toString()}`);
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        setOperators(data.results || []);
        
        // Update pagination info
        if (data.pagination) {
          setPagination({
            page: data.pagination.page,
            pageSize: data.pagination.pageSize,
            totalRecords: data.pagination.totalRecords,
            totalPages: data.pagination.totalPages,
          });
        }
        
        // Update facets
        if (data.facets) {
          setFacets(data.facets);
        }
        
        toast.success(`Loaded ${data.results.length} operators`);
      } else {
        setOperators([]);
        toast.error('No operators found');
      }
    } catch (error) {
      console.error('Load operators error:', error);
      toast.error('Failed to load operators');
      setOperators([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchParams.businessName && searchParams.states.length === 0) {
      toast.error('Please enter a business name or select at least one state');
      return;
    }

    setIsSearching(true);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    try {
      await loadOperators(1);
    } finally {
      setIsSearching(false);
    }
  };

  const handleReset = () => {
    setSearchParams({ businessName: '', states: [] });
    setPagination(prev => ({ ...prev, page: 1 }));
    loadOperators(1);
  };

  const handlePageChange = (newPage) => {
    loadOperators(newPage);
  };

  const handleSelectOperator = async (operator) => {
    // If not already assigned, add to quota first
    if (!isOperatorAssigned(operator.operatorId)) {
      try {
        const response = await apiClient.post('/Operators/assign', {
          operatorIds: [operator.operatorId],
        });

        if (response.data && response.data.success) {
          const data = response.data.data;
          
          // Update quota info
          setQuotaInfo({
            totalAssignedThisMonth: data.totalAssignedThisMonth,
            remainingQuota: data.remainingQuota,
          });
          
          // Add to assigned list
          setAssignedOperators([...assignedOperators, operator.operatorId]);
          
          if (data.addedCount > 0) {
            toast.success(`${operator.businessName || operator.corporateName} added to your quota. Remaining: ${data.remainingQuota}`);
          } else {
            toast.info(response.data.message);
          }
        } else {
          toast.error(response.data?.message || 'Failed to add operator to quota');
          return; // Don't open popup if assignment failed
        }
      } catch (error) {
        console.error('Add to quota error:', error);
        const errorMessage = error.response?.data?.message || 'Failed to add operator to quota';
        toast.error(errorMessage);
        return; // Don't open popup if assignment failed
      }
    }
    
    // Now open the details popup
    handleViewDetails(operator.operatorId);
  };

  const handleViewDetails = async (operatorId) => {
    setIsLoadingDetails(true);
    setIsDetailsDialogOpen(true);
    try {
      const response = await apiClient.get(`/operators/${operatorId}`);
      if (response.data?.success) {
        setSelectedOperatorDetails(response.data.data);
      } else {
        toast.error('Failed to load operator details');
        setIsDetailsDialogOpen(false);
      }
    } catch (error) {
      toast.error('Failed to load operator details');
      setIsDetailsDialogOpen(false);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const isOperatorAssigned = (operatorId) => {
    return assignedOperators.includes(operatorId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-foreground">Search & Assign Operators</h1>
          <p className="text-muted-foreground mt-1">
            Browse operators and add them to your subscription quota
          </p>
        </div>

        {/* Quota Info Banner */}
        {quotaInfo.totalAssignedThisMonth > 0 && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Monthly Quota Status</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {quotaInfo.totalAssignedThisMonth} assigned this month • {quotaInfo.remainingQuota} remaining
                  </p>
                </div>
                <Badge variant="secondary" className="text-lg px-4 py-2">
                  {quotaInfo.remainingQuota} Available
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Search Filters</CardTitle>
            <CardDescription>
              Filter operators by business name or state
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label htmlFor="businessName">Business Name</Label>
                <Input
                  id="businessName"
                  placeholder="Enter business name..."
                  value={searchParams.businessName}
                  onChange={(e) =>
                    setSearchParams({ ...searchParams, businessName: e.target.value })
                  }
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="state">States (Multi-Select)</Label>
                  <div className="flex items-center gap-2">
                    {searchParams.states.length < allStates.length && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-0 px-2 text-xs text-muted-foreground hover:text-primary"
                        onClick={() => setSearchParams({ 
                          ...searchParams, 
                          states: allStates.map(s => s.code) 
                        })}
                      >
                        Select All
                      </Button>
                    )}
                    {searchParams.states.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-auto py-0 px-2 text-xs text-muted-foreground hover:text-destructive"
                        onClick={() => setSearchParams({ ...searchParams, states: [] })}
                      >
                        Clear All
                      </Button>
                    )}
                  </div>
                </div>
                <Select
                  value=""
                  onValueChange={(value) => {
                    if (value && value !== "placeholder" && !searchParams.states.includes(value)) {
                      setSearchParams({ 
                        ...searchParams, 
                        states: [...searchParams.states, value] 
                      });
                    }
                  }}
                >
                  <SelectTrigger id="state">
                    <SelectValue placeholder={searchParams.states.length > 0 ? `${searchParams.states.length} state(s) selected` : "Select states..."} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder" disabled>
                      Select a state to add
                    </SelectItem>
                    {allStates.map((state) => (
                      <SelectItem 
                        key={state.code} 
                        value={state.code}
                        disabled={searchParams.states.includes(state.code)}
                      >
                        {state.name} ({state.code})
                        {searchParams.states.includes(state.code) && ' ✓'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {searchParams.states.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {searchParams.states.map((stateCode) => {
                      const state = allStates.find(s => s.code === stateCode);
                      return (
                        <Badge 
                          key={stateCode} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive hover:text-destructive-foreground transition-colors"
                          onClick={() => {
                            setSearchParams({
                              ...searchParams,
                              states: searchParams.states.filter(s => s !== stateCode)
                            });
                          }}
                        >
                          {state?.name} ({stateCode}) ×
                        </Badge>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Show available states from facets */}
              {facets.states.length > 0 && (
                <div>
                  <Label>Available States</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {facets.states.map((facet) => (
                      <Badge key={facet.name} variant="outline">
                        {facet.name} ({facet.count})
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleSearch} disabled={isSearching || isLoading}>
                {isSearching ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Searching...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={handleReset} disabled={isLoading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results Table */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Operators</CardTitle>
                <CardDescription>
                  {isLoading ? 'Loading...' : `Showing ${operators.length} of ${pagination.totalRecords} operators`} • Page {pagination.page} of {pagination.totalPages}
                </CardDescription>
              </div>
              <Badge variant="secondary">
                {assignedOperators.length} Added This Session
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : operators.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No operators found</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Try adjusting your search filters
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Business Name</TableHead>
                        <TableHead>Corporate Name</TableHead>
                        <TableHead>License Number</TableHead>
                        <TableHead>State(s)</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {operators.map((operator) => {
                        const isAssigned = isOperatorAssigned(operator.operatorId);
                        const stateDisplay = operator.state || operator.states?.[0];
                        const stateCount = operator.states?.length ?? 0;
                        return (
                          <TableRow key={operator.operatorId} className={isAssigned ? 'bg-muted/50' : ''}>
                            <TableCell className="font-medium">{operator.businessName}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">{operator.corporateName || '—'}</TableCell>
                            <TableCell>{operator.licenseNumber || '—'}</TableCell>
                            <TableCell>
                              {stateDisplay
                                ? <>
                                    <Badge variant="secondary">{stateDisplay}</Badge>
                                    {stateCount > 1 && (
                                      <div className="text-xs text-muted-foreground mt-0.5">+{stateCount - 1} more</div>
                                    )}
                                  </>
                                : <span className="text-muted-foreground">—</span>}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant={isAssigned ? "outline" : "default"}
                                  size="sm"
                                  onClick={() => handleSelectOperator(operator)}
                                >
                                  {isAssigned ? (
                                    <>
                                      <Eye className="h-4 w-4 mr-1" />
                                      View Details
                                    </>
                                  ) : (
                                    'Add to Quota'
                                  )}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {pagination.page} of {pagination.totalPages} • {pagination.totalRecords} total records
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || isLoading}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages || isLoading}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Operator Details Dialog */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col p-0 gap-0">
          {isLoadingDetails ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : selectedOperatorDetails ? (
            <OperatorDetailView
              op={selectedOperatorDetails}
              onClose={() => setIsDetailsDialogOpen(false)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Building2 className="w-12 h-12 mb-3 opacity-30" />
              <p>No details available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
